// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import * as Notation from '../notation.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import utils from '../utils.ts';
import { toHTML } from '../uTheory.Font/font.ts';
import KeySigPlayer from './keySigPlayer.ts';

class ImaginaryKeys extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.keySigPlayer = new KeySigPlayer();
    this.targetSpeedFromSkills = ImaginaryKeys.registry.skills;
    this.layout = {
      instructions: { percent: 30 },
      letters: { percent: 80 },
    };

    this.settings = {
      instructions: true,
      letters: {
        letterButtons: true,
        customButtons: [{ tagName: 'br' }],
        trashButton: true,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    super.init(displayDiv, options);
  }

  generateQuestions(questions) {
    if (this.options.drill) {
      this.questions = this.generateDrillQuestions();
    } else {
      this.questions = this.generateManualQuestions(questions);
    }

    this.questionPrompt =
      'The key of {question} has all the {question} and these double {question}:';

    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}</small>`;
    }

    this.questionPrompt += "<br /><span id='imaginaryKeyAnswerDiv'>_</span>";
    this.questionNumber = 0;
    this.answerStack = [];
  }

  generateDrillQuestions() {
    const qg = new QuestionGenerator();
    return qg.imaginary_keys(this.options.maxQuestions).map(ImaginaryKeys.processQuestion);
  }

  generateManualQuestions(questions) {
    questions = questions || this.options.questions;
    return questions.map(ImaginaryKeys.processQuestion);
  }

  /* Convert string in format of Fb/M or Db/m to a question
    @param question string
  */
  static processQuestion(question, i) {
    const tonic = question.split('/')[0];
    const quality = question.split('/')[1][0] === 'm' ? 'minor' : 'Major';
    const accidental = tonic.slice(1);
    const simpleAccidental =
      accidental === 'x'
        ? '#'
        : accidental === '#'
        ? ''
        : accidental === 'bb'
        ? 'b'
        : accidental === 'b'
        ? ''
        : '';

    const simpleKey = new Notation.KeySignature(tonic[0] + simpleAccidental, quality);
    const sharps = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
    let doubles;
    let accidentalType;
    let sharpOrFlatString;
    if (simpleKey.accidentalsCount > 0) {
      doubles = sharps.slice(0, simpleKey.accidentalsCount);
      accidentalType = 'x';
      sharpOrFlatString = 'sharps';
    } else {
      doubles = sharps.slice(simpleKey.accidentalsCount).reverse(); // because it's negative ;)
      accidentalType = 'bb';
      sharpOrFlatString = 'flats';
    }
    doubles = doubles.map((letter) => letter + accidentalType);

    const formattedKeyName = `${tonic[0]}${toHTML('accidental', accidental)} ${quality}`;

    return {
      text: [formattedKeyName, sharpOrFlatString, sharpOrFlatString, i + 1],
      keyName: question,
      formattedKeyName,
      tonic,
      quality,
      accidental: simpleKey.accidentalsCount > 0 ? 'x' : 'bb',
      doubles,
      sharpOrFlatString,
      skills: 'imaginary_keys',
      simpleKey,
    };
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
    this.letterPanel.addLetterCallback(this.addLetter, this);
    this.letterPanel.addCustomButtonCallback((keyCode) => {
      if (keyCode === 'trash') this.undo();
    }, this);
  }

  newQuestion() {
    super.newQuestion();
    this.letterPanel.setLettersAccidental(this.currentQuestion.accidental);
    this.answerStack = [];
    this.updateAnswer();
    this.wrongAnswerCount = 0;
  }

  displayInstructions() {
    super.displayInstructions();
  }

  addLetter(letter) {
    if (this.answerStack.indexOf(letter) === -1) {
      this.answerStack.push(letter + this.currentQuestion.accidental);
      this.updateAnswer();
    }
  }

  undo() {
    this.answerStack.pop();
    this.updateAnswer();
  }

  updateAnswer() {
    const answerDiv = document.getElementById('imaginaryKeyAnswerDiv');
    if (!this.answerStack.length) {
      answerDiv.removeAttribute('aria-live');
      answerDiv.innerHTML = '_';
      return;
    }
    answerDiv.setAttribute('aria-live', 'polite');
    answerDiv.innerHTML = this.answerStack.reduce(this.noteNamesArrayToFormattedString, '');
  }

  noteNamesArrayToFormattedString(a, b, i) {
    const spacer = i > 0 ? ' ' : '';
    const note = new Notation.Note(b);
    return a + spacer + note.toAccidentalFontShortString();
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;
    // When correct, call rightAnswer();
    // When incorrect, call wrongAnswer();
    if (!this.answerStack.length) {
      return this.checkMark.warn('You must indicate at least one accidental.');
    }
    const correctSet = new Set(this.currentQuestion.doubles);
    const studentAnswerSet = new Set(this.answerStack);
    const correct = utils.setCompare(correctSet, studentAnswerSet);

    if (this.options.testMode) return this.checkpointAnswer(correct);

    let hintText;
    if (this.wrongAnswerCount > 0) {
      if (correctSet.size > studentAnswerSet.size) {
        hintText = 'Too few accidentals';
      } else if (correctSet.size < studentAnswerSet.size) {
        hintText = 'Too many accidentals';
      } else {
        hintText = 'Write number of accidentals, but wrong letters.';
      }
    }
    if (this.wrongAnswerCount > 3) {
      hintText = 'The right answer is ';
      hintText += this.currentQuestion.doubles.join(', ');
    }

    if (correct) {
      this.rightAnswer(800);
    } else {
      this.wrongAnswer(hintText);
      this.wrongAnswerCount++;
    }
    return undefined;
  }

  playAnswer() {
    this.keySigPlayer.playAnswer(
      this.currentQuestion.simpleKey,
      this.currentQuestion.sharpOrFlatString === 'sharps' ? 1 : -1
    );
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      return Promise.resolve(this.newQuestion());
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct) {
    const question = this.currentQuestion;
    const questionText =
      `The key of ${question.formattedKeyName} has ` +
      `these double ${question.sharpOrFlatString}:`;
    const correctAnswerText = question.doubles.reduce(this.noteNamesArrayToFormattedString, '');

    const answerAndData = {
      question: this.currentQuestion.question,
      questionType: 'ImaginaryKeys',
      questionText,
      questionSVG: null,
      studentAnswerText: correct
        ? correctAnswerText
        : this.answerStack.reduce(this.noteNamesArrayToFormattedString, ''),
      studentAnswerSVG: null,
      correctAnswerText,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: 'imaginary_keys',
    };
    return answerAndData;
  }
}

ImaginaryKeys.registry = {
  description: 'Theoretical Keys',
  skills: ['imaginary_keys'],
  options: {
    maxQuestions: 6,
  },
};

export default ImaginaryKeys;
